import { type ReactNode, useCallback, useContext, useState } from "react";
import { Icon } from "../../Icons/Icon";
import { Spinner } from "../../Feedback/Spinner";
import { Tooltip } from "../../Feedback/Tooltip";
import { headingFontStyle } from "../../Theme/headingFontStyle";
import { ConfirmDialog } from "../../Dialog/Internal/ConfirmDialog";
import { useIsInDialogHeader } from "../../Dialog/Internal/DialogHeaderContext";
import { DialogMount_ } from "./DialogMount";
import { iconActionClasses, toneIconColors } from "./buttonStyles";
import type { ButtonProps } from "../Button";
import { MapControlContext } from "./MapControlContext";

// A cell in a Mapbox control group: centre the icon in Mapbox's 29px square,
// in the dark glyph colour of its own zoom and fullscreen icons.
const MAP_CONTROL_ICON_ACTION_CLASSES =
  "!flex items-center justify-center text-neutral-800";

/**
 * Render path for an icon-action `Button` — the compact, borderless, muted icon
 * button formerly owned by the standalone `DialogActionButton`. Activated by the
 * `iconAction` prop or by being inside a Dialog header-actions slot, so it works
 * anywhere (dialog headers, panel bodies) and coexists with bordered ControlBar
 * buttons.
 *
 * Icon actions are icon + tooltip by nature; any `children` stay `sr-only` for
 * the accessibility tree. Pass `label` instead to show a visible text label
 * beside the icon (same flat style, just discoverable) — the label then serves
 * as the accessible name and the tooltip becomes redundant. Unlike
 * StandardButton_ there is no tray routing — these dialogs (JSON viewer,
 * attachments, etc.) mount locally, matching the previous DialogActionButton
 * behavior.
 */
// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function IconActionButton_({
  icon: IconProp,
  iconWeight,
  children,
  onClick,
  tooltip,
  label,
  disabled,
  disabledMessage,
  loading,
  confirm,
  dialog,
  tone = "neutral",
  color,
  badge,
  "data-testid": dataTestid,
}: ButtonProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Loading blocks a second click the same way it does on StandardButton_.
  const isDisabled = disabled === true || loading === true;
  const hasBadge = badge !== undefined && badge > 0;
  const hasLabel = label !== undefined && label !== "";
  // In a Dialog header these icon actions take the brand-blue tint so they read
  // as deliberate, tappable controls instead of muted ghost icons (per design
  // feedback). Elsewhere (panel bodies, explicit `iconAction`) they stay muted.
  // An explicit `color` prop always wins.
  const inDialogHeader = useIsInDialogHeader();
  const inMapControl = useContext(MapControlContext);
  const restingColor = color ?? (inDialogHeader ? "text-blue-text" : undefined);

  const handleConfirm = useCallback(
    async (input?: string) => {
      setConfirmLoading(true);
      try {
        await onClick?.(input);
      } finally {
        // Close in `finally`, intentionally matching StandardButton_'s
        // useButtonConfirmation_ — not the old DialogActionButton, which closed
        // only on success (leaving the dialog open to retry on a thrown
        // onClick). All `iconAction` render paths of Button share one contract;
        // consistency with the main button beats replicating the deleted
        // component's quirk. Call sites catch their own errors regardless.
        setConfirmLoading(false);
        setConfirmOpen(false);
      }
    },
    [onClick],
  );

  const handleClick = () => {
    if (isDisabled) return;
    if (confirm) {
      setConfirmOpen(true);
      return;
    }
    if (dialog) {
      setDialogOpen(true);
    }
    // Fall through to onClick even when a dialog opened — intentionally
    // matching StandardButton_ (the old DialogActionButton returned early
    // here). A Button with both `dialog` and `onClick` fires both, in every
    // render path. No call site sets both today; this keeps the contract
    // uniform if one ever does.
    onClick?.();
  };

  const confirmConfig =
    confirm === undefined
      ? undefined
      : typeof confirm === "string"
        ? { title: confirm, message: confirm as ReactNode }
        : confirm;

  // A visible label is the accessible name, so it makes the tooltip redundant —
  // only fall back to tooltip/children for the icon-only case. disabledMessage
  // still wins so a disabled action can always explain itself.
  const effectiveTooltip: ReactNode =
    disabled === true && disabledMessage
      ? disabledMessage
      : hasLabel
        ? tooltip
        : (tooltip ?? children);

  const button = (
    <button
      type="button"
      onClick={handleClick}
      tabIndex={-1}
      disabled={isDisabled}
      // Styled from `disabled` alone: the 40% fade would wash out the spinner.
      // In a map control group Mapbox already sizes, colours the hover of and
      // forces `display: block` on the button, so only the centring is ours.
      className={
        inMapControl
          ? // Mapbox only fades its own `.mapboxgl-ctrl-icon` when disabled,
            // which our Icon isn't, so the disabled look is ours to keep.
            `${MAP_CONTROL_ICON_ACTION_CLASSES}${disabled === true ? " cursor-not-allowed opacity-40" : ""}`
          : iconActionClasses(
              restingColor,
              disabled === true,
              hasBadge || hasLabel,
            )
      }
      style={headingFontStyle()}
      {...(dataTestid !== undefined && { "data-testid": dataTestid })}
    >
      {loading ? (
        <Spinner size={14} />
      ) : (
        IconProp && (
          <Icon
            icon={IconProp}
            size={inMapControl ? "lg" : "sm"}
            weight={iconWeight ?? "bold"}
            {...(restingColor && !inMapControl ? { color: restingColor } : {})}
          />
        )
      )}
      {hasBadge && <span className="text-xs font-semibold">{badge}</span>}
      {hasLabel ? (
        <span className="text-xs font-medium">{label}</span>
      ) : (
        children && <span className="sr-only">{children}</span>
      )}
    </button>
  );

  const buttonWithTooltip = effectiveTooltip ? (
    <Tooltip content={effectiveTooltip}>{button}</Tooltip>
  ) : (
    button
  );

  return (
    <>
      {buttonWithTooltip}
      {confirm && confirmConfig && (
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirm}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmLabel={confirmConfig.confirmLabel ?? "Confirm"}
          cancelLabel={confirmConfig.cancelLabel}
          confirmTone={tone}
          icon={confirmConfig.icon}
          iconColors={toneIconColors[confirmConfig.iconTone ?? tone]}
          loading={confirmLoading}
          inputLabel={confirmConfig.inputLabel}
          inputPlaceholder={confirmConfig.inputPlaceholder}
          inputDefaultValue={confirmConfig.inputDefaultValue}
        />
      )}
      {dialog && (
        <DialogMount_
          dialog={dialog}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      )}
    </>
  );
}
